import { DbExtension, AdapterBase, pathToLog } from 'pqb/internal';
import {
  makeFileVersion,
  makeStructureToAstCtx,
  RakeDbAst,
  RakeDbConfig,
  saveMigratedVersion,
  SilentQueries,
  structureToAst,
} from 'rake-db';
import fs from 'fs/promises';
import path from 'node:path';
import {
  AppCodeGenTable,
  appCodeGenTable,
  AppCodeGenTables,
  getTableInfosAndFKeys,
} from './appCodeGenerators/tables.appCodeGenerator';
import { appCodeGenUpdateDbFile } from './appCodeGenerators/dbFile.appCodeGenerator';
import { generate } from '../generate/generate';

export const pull = async (adapters: AdapterBase[], config: RakeDbConfig) => {
  if (!config.dbPath || !config.baseTable) {
    throw new Error(
      `\`${
        config.dbPath ? 'baseTable' : 'dbPath'
      }\` setting must be set in the migrations config for pull command`,
    );
  }

  const baseTablePath = config.baseTable.getFilePath();
  const baseTableExportedAs = config.baseTable.exportAs;

  const [adapter] = adapters;
  const adapterSchema = adapter.getSchema();
  const currentSchema =
    (typeof adapterSchema === 'function' ? adapterSchema() : adapterSchema) ??
    'public';

  const ctx = makeStructureToAstCtx(config, currentSchema);

  const asts = await structureToAst(ctx, adapter, config);

  const { tableInfos, fkeys } = getTableInfosAndFKeys(asts, config);

  const exclusiveWriteOptions = { flag: 'wx' as const };
  const pendingFileWrites: [
    path: string,
    content: string,
    options?: { flag: 'wx' },
  ][] = [];

  const tables: AppCodeGenTables = {};
  const extensions: DbExtension[] = [];
  const domains: RakeDbAst.Domain[] = [];

  let firstTable: undefined | AppCodeGenTable;
  for (const ast of asts) {
    switch (ast.type) {
      case 'table': {
        const table = appCodeGenTable(
          tableInfos,
          fkeys,
          ast,
          baseTablePath,
          baseTableExportedAs,
          currentSchema,
        );
        tables[table.key] = table;
        if (!firstTable) firstTable = table;
        pendingFileWrites.push([
          table.path,
          table.content,
          exclusiveWriteOptions,
        ]);
        break;
      }
      case 'extension': {
        extensions.push({
          name: ast.schema ? `${ast.schema}.${ast.name}` : ast.name,
          version: ast.version,
        });
        break;
      }
      case 'domain': {
        domains.push(ast);
        break;
      }
    }
  }

  if (!firstTable && !extensions.length && !domains.length) {
    await adapter.close();
    return;
  }

  let dbPath = path.resolve(config.basePath, config.dbPath);
  if (!dbPath.endsWith('.ts')) dbPath += '.ts';
  const content = await appCodeGenUpdateDbFile(
    dbPath,
    tables,
    extensions,
    domains,
    currentSchema,
  );
  if (content) pendingFileWrites.push([dbPath, content]);

  if (firstTable) {
    await fs.mkdir(path.dirname(firstTable.path), { recursive: true });
  }

  await Promise.all(
    pendingFileWrites.map(([path, content, options]) =>
      fs.writeFile(path, content, options).then(() => {
        config.logger?.log(`Created ${pathToLog(path)}`);
      }),
    ),
  );

  const version = await makeFileVersion({}, config);
  await generate(adapters, config, ['pull'], { adapter, version });

  // save migrated version into all configured databases
  await Promise.all(
    adapters.map(async (adapter) => {
      const silentAdapter = adapter as unknown as SilentQueries;
      silentAdapter.silentArrays = adapter.arrays;
      await saveMigratedVersion(silentAdapter, version, 'pull.ts', config);
      await adapter.close();
    }),
  );
};
